import { Link } from "react-router-dom";
import { ShoppingCart, Heart, Star, Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductCard({ product, wishlistIds = [], onWishlistToggle }) {
  const { user, addToCart } = useAuth();
  const isWished = wishlistIds.includes(product.product_id);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await addToCart(product, 1);
      toast.success(`${product.name} added to cart`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add to cart");
    }
  };

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("Please login first"); return; }
    try {
      await axios.post(`${API}/user/wishlist/${product.product_id}`, {}, { withCredentials: true });
      onWishlistToggle?.(product.product_id);
    } catch {}
  };

  return (
    <Link to={`/products/${product.product_id}`} className="block h-full" data-testid={`product-card-${product.product_id}`}>
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden card-3d-inner group h-full flex flex-col">
      <div className="relative aspect-square bg-slate-50 overflow-hidden shrink-0">
        <img src={product.images?.[0] || `https://placehold.co/400x400/e0f2fe/0891b2?text=${encodeURIComponent(product.name)}`} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        {product.discount_percent > 0 && (
          <Badge className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5">{product.discount_percent}% OFF</Badge>
        )}
        {product.requires_prescription && (
          <Badge className="absolute top-2 right-10 bg-amber-500 text-white text-[10px] rounded-full px-2 py-0.5"><Shield className="w-3 h-3 inline mr-0.5" />Rx</Badge>
        )}
        <button onClick={toggleWishlist} className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all ${isWished ? 'bg-red-50 text-red-500' : 'bg-white/80 text-slate-400 hover:text-red-500'}`} data-testid={`wishlist-btn-${product.product_id}`}>
          <Heart className={`w-3.5 h-3.5 ${isWished ? 'fill-current' : ''}`} />
        </button>
      </div>
      <div className="p-3 flex flex-col flex-1">
        <p className="text-[11px] text-cyan-600 font-medium mb-0.5 truncate">{product.brand}</p>
        <h3 className="text-xs sm:text-sm font-semibold text-slate-800 line-clamp-2 mb-1.5 leading-snug flex-1">{product.name}</h3>
        <div className="flex items-center gap-1 mb-1.5">
          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
          <span className="text-[11px] font-medium text-slate-700">{product.rating}</span>
          <span className="text-[11px] text-slate-400">({product.review_count})</span>
        </div>
        <div className="flex items-center justify-between gap-1 mt-auto">
          <div className="min-w-0">
            <span className="text-sm sm:text-base font-bold text-slate-800">Rs.{product.sale_price}</span>
            {product.mrp > product.sale_price && (
              <span className="text-[10px] text-slate-400 line-through ml-1">Rs.{product.mrp}</span>
            )}
          </div>
          <button onClick={handleAddToCart} className="flex items-center gap-1 px-2.5 py-1.5 bg-cyan-600 text-white text-[11px] font-semibold rounded-full hover:bg-cyan-700 transition-all active:scale-95 shrink-0" data-testid={`add-cart-btn-${product.product_id}`}>
            <ShoppingCart className="w-3 h-3" /> Add
          </button>
        </div>
      </div>
    </div></Link>
  );
}
